import React, { useState } from 'react';
import {
  User,
  Settings,
  CreditCard,
  BookOpen,
  Mail,
  LogOut,
  Truck,
  MessageCircle,
  Bell,
  Plus,
  Check,
  Zap,
} from 'lucide-react';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TutorialEngine } from '@/components/tutorial/TutorialEngine';
import { cn } from '@/lib/utils';

const INITIAL_COMPANY_NAME = 'RUTAPP';

const PROFILES = [
  { id: 'SARA', own: true },
  { id: 'ANA', own: false },
  { id: 'DANI', own: false },
  { id: 'MANU', own: false },
];

const PROFILE_STATUS = {
  SARA: 'go',
  ANA: 'salimos',
  DANI: 'volvemos',
  MANU: 'fin',
};

const ANA_CLIENTS = ['KIRA', 'LOLA', 'MAX'];

const STATUS_META = {
  go: { name: 'Go', colorName: 'Verde', solid: '#00c800', bg: 'rgba(0, 200, 0, 0.25)' },
  salimos: { name: 'Salimos', colorName: 'Azul', solid: '#007bff', bg: 'rgba(0, 123, 255, 0.25)' },
  volvemos: { name: 'Volvemos', colorName: 'Amarillo', solid: '#e0be00', bg: 'rgba(255, 215, 0, 0.25)' },
  fin: { name: 'Fin', colorName: 'Rojo', solid: '#ff0000', bg: 'rgba(255, 0, 0, 0.25)' },
};

const STEPS = [
  { id: 'account-action' },
  { id: 'account-menu-info' },
  { id: 'settings-action' },
  { id: 'settings-overview' },
  { id: 'company-name-action' },
  { id: 'company-save' },
  { id: 'company-result' },
  { id: 'profiles-info' },
  { id: 'link-account-info' },
  { id: 'visibility-action' },
  { id: 'visibility-result' },
  { id: 'client-access-action' },
  { id: 'client-access-result' },
  { id: 'communication-info' },
  { id: 'whatsapp-toggle' },
  { id: 'alerts-toggle' },
  { id: 'states-toggle' },
  { id: 'state-count' },
  { id: 'state-config-info' },
  { id: 'admin-pin-info' },
  { id: 'plans-action' },
  { id: 'premium-pro-info' },
  { id: 'profiles-plan-action' },
  { id: 'profiles-plan-result' },
  { id: 'tutorial-complete' },
];

const SETTINGS_STEPS = new Set([
  'settings-overview',
  'company-name-action',
  'company-save',
  'profiles-info',
  'link-account-info',
  'visibility-action',
  'visibility-result',
  'client-access-action',
  'client-access-result',
  'communication-info',
  'whatsapp-toggle',
  'alerts-toggle',
  'states-toggle',
  'state-count',
  'state-config-info',
  'admin-pin-info',
  'plans-action',
]);

const PLANS_STEPS = new Set([
  'premium-pro-info',
  'profiles-plan-action',
  'profiles-plan-result',
]);

const PRO_BENEFITS = [
  'Varios perfiles',
  'Clientes ilimitados',
  'Todo lo de Premium Personal',
  'Alertas entre perfiles',
  'WhatsApp entre equipos',
  'Estados compartidos entre perfiles',
];

function getStateKeys(count) {
  if (count === '2') return ['go', 'fin'];
  if (count === '3') return ['go', 'salimos', 'fin'];
  return ['go', 'salimos', 'volvemos', 'fin'];
}

function formatEuro(value) {
  return value.toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function computeProPrice(profiles) {
  const monthly = 9.95 + (profiles - 1) * 7.95;
  const yearly = monthly * 12 * 0.8;
  return { monthly, yearly, yearlyMonthly: yearly / 12 };
}

function CoachMark({ text }) {
  if (!text) return null;
  return (
    <div className="flex justify-center">
      <div className="max-w-[340px] rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs font-medium text-foreground shadow-sm">
        {text}
      </div>
    </div>
  );
}

function ContinueButton({ onClick }) {
  return (
    <div className="flex justify-center">
      <Button type="button" size="sm" onClick={onClick}>
        Continuar
      </Button>
    </div>
  );
}

function SwitchSim({ checked, onToggle, halo, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onToggle}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
        checked ? 'bg-primary' : 'bg-muted',
        halo && 'ring-4 ring-amber-400 ring-offset-2'
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5'
        )}
      />
    </button>
  );
}

function SwitchRow({ label, helper, checked, onToggle, halo }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {helper ? <p className="text-xs text-muted-foreground">{helper}</p> : null}
      </div>
      <SwitchSim checked={checked} onToggle={onToggle} halo={halo} label={label} />
    </div>
  );
}

function ProfileChip({ id, statusKey, own, showWhatsApp, halo, onClick, small }) {
  const meta = STATUS_META[statusKey];
  return (
    <div className="relative flex flex-col items-center">
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className={cn(
          'flex flex-col items-center gap-1 rounded-lg p-1',
          halo && 'ring-4 ring-amber-400 ring-offset-2'
        )}
      >
        <div
          className={cn(
            'flex items-center justify-center rounded-full border-2',
            small ? 'h-10 w-10' : 'h-12 w-12'
          )}
          style={{ backgroundColor: meta.bg, borderColor: meta.solid }}
        >
          <Truck
            className={small ? 'h-5 w-5' : 'h-6 w-6'}
            style={{ color: meta.solid }}
            strokeWidth={2}
          />
        </div>
        <span className="text-xs font-semibold text-slate-900">{id}</span>
      </button>
      {own ? (
        <span className="mt-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-semibold leading-none text-primary-foreground">
          TU PERFIL
        </span>
      ) : null}
      {showWhatsApp ? (
        <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#5FCB87]">
          <MessageCircle className="h-3.5 w-3.5 text-white" />
        </span>
      ) : null}
    </div>
  );
}

function MenuRow({ icon: Icon, label, halo, onClick }) {
  const baseClass = cn(
    'flex items-center justify-start gap-2 rounded-md border px-3 py-2 text-sm text-foreground',
    onClick && 'transition-colors hover:bg-accent/50',
    halo && 'border-amber-400 ring-4 ring-amber-400 ring-offset-2'
  );

  if (!onClick) {
    return (
      <div className={baseClass}>
        <Icon className="h-4 w-4" />
        {label}
      </div>
    );
  }

  return (
    <button type="button" onClick={onClick} className={baseClass}>
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function SettingsCard({ title, children }) {
  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      {children}
    </div>
  );
}

function PremiumProCard({ profiles, cycle, selectable, haloSelector, onProfilesChange }) {
  const price = computeProPrice(profiles);
  const monthly = cycle === 'monthly';
  return (
    <div className="space-y-3 rounded-xl border-2 border-blue-500/40 bg-[#13151F] p-4">
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-blue-400" />
        <h3 className="text-base font-semibold text-white">Premium Pro</h3>
      </div>

      <div className="flex gap-1">
        <span
          className={cn(
            'rounded px-2 py-1 text-xs',
            cycle === 'monthly' ? 'bg-blue-600 text-white' : 'bg-[#242424] text-[#909090]'
          )}
        >
          Mensual
        </span>
        <span
          className={cn(
            'rounded px-2 py-1 text-xs',
            cycle === 'yearly' ? 'bg-blue-600 text-white' : 'bg-[#242424] text-[#909090]'
          )}
        >
          Anual · -20%
        </span>
      </div>

      <div>
        <label className="mb-1 block text-xs text-[#909090]">Número de perfiles</label>
        {selectable ? (
          <select
            value={profiles}
            onChange={(e) => onProfilesChange(Number(e.target.value))}
            className={cn(
              'w-full rounded-md border border-[#3A3A3A] bg-[#242424] px-3 py-2 text-sm text-white',
              haloSelector && 'ring-4 ring-amber-400 ring-offset-2'
            )}
          >
            {Array.from({ length: 9 }, (_, i) => i + 2).map((n) => (
              <option key={n} value={n}>
                {n} perfiles
              </option>
            ))}
          </select>
        ) : (
          <div className="w-full rounded-md border border-[#3A3A3A] bg-[#242424] px-3 py-2 text-sm text-white">
            {profiles} perfiles
          </div>
        )}
      </div>

      <div>
        {monthly ? (
          <p className="text-lg font-bold text-white">{formatEuro(price.monthly)} €/mes</p>
        ) : (
          <div>
            <p className="text-lg font-bold text-white">{formatEuro(price.yearlyMonthly)} €/mes</p>
            <p className="text-xs text-[#909090]">Facturado {formatEuro(price.yearly)} €/año</p>
          </div>
        )}
      </div>

      <ul className="space-y-2 text-sm text-[#C0C8D8]">
        {PRO_BENEFITS.map((benefit) => (
          <li key={benefit} className="flex items-center gap-2">
            <Check className="h-3.5 w-3.5 shrink-0 text-blue-400" />
            {benefit}
          </li>
        ))}
      </ul>

      <div className="space-y-1 border-t border-[#1E1E1E] pt-2">
        <button type="button" className="w-full text-left text-sm text-[#606060]">
          Restaurar compra
        </button>
        <button type="button" className="w-full text-left text-sm text-[#606060]">
          Gestionar suscripción
        </button>
      </div>
    </div>
  );
}

function SimulatedComplete({ onRestartTutorial, onExitTutorial, exitLabel = 'Volver a Tutoriales' }) {
  return (
    <div className="rounded-lg border bg-background p-6 text-center">
      <h3 className="text-lg font-semibold">Has completado Acciones de administrador.</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Ya sabes cómo configurar tu empresa, gestionar perfiles y cuentas, definir la
        visibilidad y comunicación del equipo y adaptar Premium Pro al número de perfiles
        que necesitas.
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button type="button" variant="outline" onClick={onRestartTutorial}>
          Repetir tutorial
        </Button>
        <Button type="button" onClick={onExitTutorial}>
          {exitLabel}
        </Button>
      </div>
    </div>
  );
}

export function AdminActionsTutorial({ onExit, completionActionLabel, onCompletionAction }) {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [companyName, setCompanyName] = useState(INITIAL_COMPANY_NAME);
  const [savedCompanyName, setSavedCompanyName] = useState(INITIAL_COMPANY_NAME);
  const [showAllProfilesInfo, setShowAllProfilesInfo] = useState(false);
  const [allowCrossProfileClientsConsultation, setAllowCrossProfileClientsConsultation] = useState(false);
  const [shareWhatsAppAccess, setShareWhatsAppAccess] = useState(true);
  const [shareFleetAlerts, setShareFleetAlerts] = useState(true);
  const [colorCodeEnabled, setColorCodeEnabled] = useState(true);
  const [whatsappRestored, setWhatsappRestored] = useState(false);
  const [alertsRestored, setAlertsRestored] = useState(false);
  const [statesRestored, setStatesRestored] = useState(false);
  const [routeStateCount, setRouteStateCount] = useState('4');
  const [stateCountReturnPrompt, setStateCountReturnPrompt] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [selectedPlanProfiles, setSelectedPlanProfiles] = useState(3);
  const [proCycle, setProCycle] = useState('yearly');
  const [clientConsultOpen, setClientConsultOpen] = useState(false);

  const resetScenario = () => {
    setAccountMenuOpen(false);
    setSettingsOpen(false);
    setCompanyName(INITIAL_COMPANY_NAME);
    setSavedCompanyName(INITIAL_COMPANY_NAME);
    setShowAllProfilesInfo(false);
    setAllowCrossProfileClientsConsultation(false);
    setShareWhatsAppAccess(true);
    setShareFleetAlerts(true);
    setColorCodeEnabled(true);
    setWhatsappRestored(false);
    setAlertsRestored(false);
    setStatesRestored(false);
    setRouteStateCount('4');
    setStateCountReturnPrompt(false);
    setPlansOpen(false);
    setSelectedPlanProfiles(3);
    setProCycle('yearly');
    setClientConsultOpen(false);
  };

  const handleBack = (targetStep) => {
    if (!targetStep) return;

    switch (targetStep.id) {
      // Cuenta / ajustes
      case 'account-action':
        setAccountMenuOpen(false);
        break;
      case 'account-menu-info':
        break;
      case 'settings-action':
        setAccountMenuOpen(true);
        setSettingsOpen(false);
        break;
      case 'settings-overview':
        setSavedCompanyName(INITIAL_COMPANY_NAME);
        setCompanyName(INITIAL_COMPANY_NAME);
        break;

      // Nombre de empresa
      case 'company-name-action':
        setCompanyName(INITIAL_COMPANY_NAME);
        break;
      case 'company-save':
        setSettingsOpen(true);
        break;
      case 'company-result':
        setSettingsOpen(false);
        break;

      // Perfiles / vinculación
      case 'profiles-info':
      case 'link-account-info':
        break;

      // Visibilidad
      case 'visibility-action':
        setShowAllProfilesInfo(false);
        break;
      case 'visibility-result':
        break;

      // Acceso a clientes de otros perfiles
      case 'client-access-action':
        setAllowCrossProfileClientsConsultation(false);
        break;
      case 'client-access-result':
        setClientConsultOpen(false);
        break;

      // Comunicación
      case 'communication-info':
        break;
      case 'whatsapp-toggle':
        setShareWhatsAppAccess(true);
        setWhatsappRestored(false);
        break;
      case 'alerts-toggle':
        setShareFleetAlerts(true);
        setAlertsRestored(false);
        break;
      case 'states-toggle':
        setColorCodeEnabled(true);
        setStatesRestored(false);
        break;

      // Cantidad de estados
      case 'state-count':
        setRouteStateCount('4');
        setStateCountReturnPrompt(false);
        break;
      case 'state-config-info':
      case 'admin-pin-info':
        break;

      // Planes
      case 'plans-action':
        setSettingsOpen(true);
        setPlansOpen(false);
        break;
      case 'premium-pro-info':
        break;
      case 'profiles-plan-action':
        setSelectedPlanProfiles(3);
        break;
      case 'profiles-plan-result':
        break;
      case 'tutorial-complete':
        break;

      default:
        break;
    }
  };

  const renderStep = (step, index, actions) => {
    const goNext = actions?.goNext ?? (() => {});
    const restart = actions?.restart ?? (() => {});
    const exit = actions?.exit ?? (() => {});
    const completeAction = onCompletionAction ?? exit;
    const completeLabel = completionActionLabel ?? 'Volver a Tutoriales';
    const handleContinue = () => goNext();

    const showMenu = accountMenuOpen && (step.id === 'account-menu-info' || step.id === 'settings-action');
    const showSettings = settingsOpen && SETTINGS_STEPS.has(step.id);
    const showPlans = plansOpen && PLANS_STEPS.has(step.id);

    const handleCompanyNameChange = (e) => {
      const value = e.target.value;
      setCompanyName(value);
      if (step.id === 'company-name-action') {
        const trimmed = value.trim();
        if (trimmed !== '' && trimmed !== INITIAL_COMPANY_NAME) {
          goNext();
        }
      }
    };

    const handleSaveCompanyName = () => {
      if (step.id === 'company-save') {
        setSavedCompanyName(companyName.trim() || INITIAL_COMPANY_NAME);
        setSettingsOpen(false);
        goNext();
      }
    };

    const renderMainScreen = (haloAccount) => (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
          <div className="h-10 w-10" />
          <div className="text-xl font-bold text-slate-900">{savedCompanyName}</div>
          <button
            type="button"
            onClick={() => {
              if (step.id === 'account-action') {
                setAccountMenuOpen(true);
                goNext();
              }
            }}
            aria-label="Abrir Cuenta"
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full text-primary',
              haloAccount && 'ring-4 ring-amber-400 ring-offset-2'
            )}
          >
            <User className="h-6 w-6" />
          </button>
        </div>
        <div className="mt-4 flex items-start justify-center gap-3">
          {PROFILES.map((p) => (
            <ProfileChip key={p.id} id={p.id} statusKey={PROFILE_STATUS[p.id]} own={p.own} />
          ))}
        </div>
      </div>
    );

    const renderAccountMenu = (haloSettings) => (
      <div className="rounded-lg border bg-card p-4 shadow-sm">
        <h3 className="text-base font-semibold text-foreground">Cuenta</h3>
        <p className="text-xs text-muted-foreground">Rol: Admin</p>
        <div className="mt-3 flex flex-col gap-2">
          <MenuRow
            icon={Settings}
            label="Ajustes generales"
            halo={haloSettings}
            onClick={
              haloSettings
                ? () => {
                    setAccountMenuOpen(false);
                    setSettingsOpen(true);
                    goNext();
                  }
                : undefined
            }
          />
          <MenuRow icon={CreditCard} label="Ver planes y precios" />
          <MenuRow icon={BookOpen} label="Tutoriales" />
          <MenuRow icon={Mail} label="Contactar con soporte" />
          <MenuRow icon={LogOut} label="Cerrar sesión" />
        </div>
      </div>
    );

    if (step.id === 'account-action') {
      return (
        <div className="space-y-3">
          {renderMainScreen(true)}
          <CoachMark text="Pulsa el icono de cuenta para acceder a las opciones de administrador." />
        </div>
      );
    }

    if (showMenu) {
      return (
        <div className="space-y-3">
          {renderAccountMenu(step.id === 'settings-action')}
          <CoachMark
            text={
              step.id === 'settings-action'
                ? 'Pulsa Ajustes generales para configurar tu equipo.'
                : 'Desde aquí el administrador puede acceder a los ajustes generales, planes y otras opciones.'
            }
          />
          {step.id === 'account-menu-info' ? <ContinueButton onClick={handleContinue} /> : null}
        </div>
      );
    }

    if (showPlans) {
      if (step.id === 'premium-pro-info') {
        return (
          <div className="space-y-3">
            <PremiumProCard profiles={selectedPlanProfiles} cycle={proCycle} />
            <CoachMark text="Premium Pro se adapta al número de perfiles que necesita tu equipo." />
            <ContinueButton onClick={handleContinue} />
          </div>
        );
      }

      if (step.id === 'profiles-plan-action') {
        return (
          <div className="space-y-3">
            <PremiumProCard
              profiles={selectedPlanProfiles}
              cycle={proCycle}
              selectable
              haloSelector
              onProfilesChange={(n) => {
                setSelectedPlanProfiles(n);
                if (n !== 3) {
                  goNext();
                }
              }}
            />
            <CoachMark text="Pulsa el selector y elige otro número de perfiles." />
          </div>
        );
      }

      return (
        <div className="space-y-3">
          <PremiumProCard profiles={selectedPlanProfiles} cycle={proCycle} />
          <CoachMark text="Premium Pro se adapta al número de perfiles y el precio cambia según el tamaño del equipo. Cambiar este selector ajusta el plan disponible, pero no crea automáticamente nuevos perfiles: los perfiles se crean y vinculan desde Configuración. Actualmente Premium Pro permite configurar hasta 10 perfiles." />
          <ContinueButton onClick={handleContinue} />
        </div>
      );
    }

    if (showSettings) {
      if (step.id === 'company-name-action' || step.id === 'company-save') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Datos de Empresa">
              <div>
                <p className="text-sm font-medium text-foreground">Nombre de la Empresa</p>
                <input
                  value={companyName}
                  maxLength={15}
                  onChange={handleCompanyNameChange}
                  placeholder="Escribe el nombre de tu empresa"
                  className={cn(
                    'mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
                    step.id === 'company-name-action' && 'ring-4 ring-amber-400 ring-offset-2'
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveCompanyName}
                  className={cn(step.id === 'company-save' && 'ring-4 ring-amber-400 ring-offset-2')}
                >
                  Guardar Cambios
                </Button>
              </div>
            </SettingsCard>
            <CoachMark
              text={
                step.id === 'company-name-action'
                  ? 'Escribe un nuevo nombre para tu empresa.'
                  : 'Pulsa Guardar Cambios para aplicar el nuevo nombre.'
              }
            />
          </div>
        );
      }

      if (step.id === 'profiles-info') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Edición de perfiles">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Perfiles</p>
                <Button type="button" size="sm">
                  <Plus className="mr-2 h-4 w-4" /> Añadir
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Añade nuevos perfiles o elimina los que no necesites.</p>
              <div className="space-y-2">
                <Button type="button" variant="outline" size="sm" className="w-full">
                  Mostrar perfiles
                </Button>
                <Button type="button" variant="outline" size="sm" className="w-full">
                  Desplegar perfiles
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {PROFILES.map((p) => (
                  <div key={p.id} className="rounded-md border bg-muted/30 p-2 text-sm text-foreground">
                    {p.id}
                  </div>
                ))}
              </div>
            </SettingsCard>
            <CoachMark text="Aquí puedes crear los perfiles que forman parte de tu equipo. Un perfil no es lo mismo que una cuenta de usuario. Primero creas el perfil y después puedes vincularle la cuenta del trabajador." />
            <ContinueButton onClick={handleContinue} />
          </div>
        );
      }

      if (step.id === 'link-account-info') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Vincular cuenta">
              <p className="text-xs text-muted-foreground">
                La cuenta debe haberse registrado y verificado su correo antes de vincularla.
              </p>
              <div>
                <p className="text-sm font-medium text-foreground">Email de la cuenta</p>
                <div className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground">
                  trabajador@ejemplo.com
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Perfil a asignar</p>
                <div className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground">
                  ANA
                </div>
              </div>
              <Button type="button" className="w-full">
                Vincular cuenta
              </Button>
            </SettingsCard>
            <CoachMark text="El trabajador crea primero su propia cuenta y verifica su correo o accede con Google. Después el administrador crea el perfil y vincula esa cuenta introduciendo su email y eligiendo el perfil. La cuenta vinculada pasa a formar parte del equipo con rol User." />
            <ContinueButton onClick={handleContinue} />
          </div>
        );
      }

      if (step.id === 'visibility-action' || step.id === 'visibility-result') {
        const isAction = step.id === 'visibility-action';
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Visibilidad entre perfiles">
              <SwitchRow
                label="Hacer visibles todos los perfiles y sus estados"
                helper="Permite que los usuarios vean los demás perfiles y los colores de sus estados."
                checked={showAllProfilesInfo}
                onToggle={
                  isAction
                    ? () => {
                        setShowAllProfilesInfo(true);
                        goNext();
                      }
                    : () => {}
                }
                halo={isAction}
              />
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-center gap-3">
                  {(showAllProfilesInfo ? PROFILES : [PROFILES[0]]).map((p) => (
                    <ProfileChip
                      key={p.id}
                      id={p.id}
                      statusKey={PROFILE_STATUS[p.id]}
                      own={p.own}
                      small
                    />
                  ))}
                </div>
              </div>
            </SettingsCard>
            <CoachMark
              text={
                isAction
                  ? 'Con esta opción desactivada, cada usuario solo ve su propio perfil. Actívala para ver cómo cambia.'
                  : 'Al activarla, los usuarios pueden ver los demás perfiles y sus estados, pero esto no permite consultar sus clientes.'
              }
            />
            {!isAction ? <ContinueButton onClick={handleContinue} /> : null}
          </div>
        );
      }

      if (step.id === 'client-access-action' || step.id === 'client-access-result') {
        const isAction = step.id === 'client-access-action';
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Visibilidad entre perfiles">
              <SwitchRow
                label="Permitir consultar los clientes de otros perfiles"
                helper="Los usuarios podrán abrir otros perfiles y ver sus clientes, pero no realizar cambios."
                checked={allowCrossProfileClientsConsultation}
                onToggle={
                  isAction
                    ? () => {
                        setAllowCrossProfileClientsConsultation(true);
                        goNext();
                      }
                    : () => {}
                }
                halo={isAction}
              />
              {!isAction ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center justify-center gap-3">
                    {PROFILES.map((p) => (
                      <ProfileChip
                        key={p.id}
                        id={p.id}
                        statusKey={PROFILE_STATUS[p.id]}
                        own={p.own}
                        small
                        halo={p.id === 'ANA' && !clientConsultOpen}
                        onClick={p.id === 'ANA' ? () => setClientConsultOpen(true) : undefined}
                      />
                    ))}
                  </div>
                  {clientConsultOpen ? (
                    <div className="mt-4 rounded-md border bg-card p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground">ANA</p>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          Solo consulta
                        </span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {ANA_CLIENTS.map((client) => (
                          <div key={client} className="rounded-md bg-muted/30 px-3 py-1.5 text-sm text-foreground">
                            {client}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </SettingsCard>
            <CoachMark
              text={
                isAction
                  ? 'Activa esta opción para permitir la consulta de clientes de otros perfiles.'
                  : clientConsultOpen
                    ? 'Puedes consultar sus clientes, pero no realizar cambios ni utilizar sus acciones.'
                    : 'Pulsa ANA para consultar sus clientes.'
              }
            />
            {!isAction && clientConsultOpen ? <ContinueButton onClick={handleContinue} /> : null}
          </div>
        );
      }

      if (step.id === 'communication-info') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Comunicación entre perfiles">
              <SwitchRow label="Compartir acceso directo a WhatsApp" checked onToggle={() => {}} />
              <div className="border-t" />
              <SwitchRow label="Compartir avisos de flota" checked onToggle={() => {}} />
              <SwitchRow label="Activar botones de estado entre perfiles" checked onToggle={() => {}} />
            </SettingsCard>
            <CoachMark text="Desde aquí el administrador decide qué herramientas de comunicación estarán disponibles para el equipo." />
            <ContinueButton onClick={handleContinue} />
          </div>
        );
      }

      if (step.id === 'whatsapp-toggle') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Comunicación entre perfiles">
              <SwitchRow
                label="Compartir acceso directo a WhatsApp"
                checked={shareWhatsAppAccess}
                onToggle={
                  whatsappRestored
                    ? undefined
                    : () => {
                        const next = !shareWhatsAppAccess;
                        setShareWhatsAppAccess(next);
                        if (next) {
                          setWhatsappRestored(true);
                        }
                      }
                }
                halo={!whatsappRestored}
              />
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-center gap-3">
                  <ProfileChip
                    id="ANA"
                    statusKey={PROFILE_STATUS.ANA}
                    showWhatsApp={shareWhatsAppAccess}
                    small
                  />
                </div>
              </div>
            </SettingsCard>
            {whatsappRestored ? (
              <CoachMark text="Perfecto. El acceso directo a WhatsApp vuelve a estar disponible para los perfiles que tengan un teléfono configurado." />
            ) : (
              <CoachMark
                text={
                  shareWhatsAppAccess
                    ? 'Desactiva el acceso directo a WhatsApp para ver cómo desaparece.'
                    : 'Vuelve a activarlo para ver cómo aparece de nuevo.'
                }
              />
            )}
            {whatsappRestored ? <ContinueButton onClick={handleContinue} /> : null}
          </div>
        );
      }

      if (step.id === 'alerts-toggle') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Comunicación entre perfiles">
              <SwitchRow
                label="Compartir avisos de flota"
                checked={shareFleetAlerts}
                onToggle={
                  alertsRestored
                    ? undefined
                    : () => {
                        const next = !shareFleetAlerts;
                        setShareFleetAlerts(next);
                        if (next) {
                          setAlertsRestored(true);
                        }
                      }
                }
                halo={!alertsRestored}
              />
              <div className="flex items-center justify-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                {shareFleetAlerts ? (
                  <Bell className="h-7 w-7" stroke="#000000" fill="#fff191" strokeWidth={0.8} />
                ) : (
                  <span className="text-xs text-slate-400">Sin campana de avisos</span>
                )}
              </div>
            </SettingsCard>
            {alertsRestored ? (
              <CoachMark text="Perfecto. La campana vuelve a estar disponible para enviar avisos entre perfiles." />
            ) : (
              <CoachMark
                text={
                  shareFleetAlerts
                    ? 'Desactiva la campana para ver cómo desaparece.'
                    : 'Vuelve a activarla para ver cómo aparece de nuevo.'
                }
              />
            )}
            {alertsRestored ? <ContinueButton onClick={handleContinue} /> : null}
          </div>
        );
      }

      if (step.id === 'states-toggle') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Comunicación entre perfiles">
              <SwitchRow
                label="Activar botones de estado entre perfiles"
                checked={colorCodeEnabled}
                onToggle={
                  statesRestored
                    ? undefined
                    : () => {
                        const next = !colorCodeEnabled;
                        setColorCodeEnabled(next);
                        if (next) {
                          setStatesRestored(true);
                        }
                      }
                }
                halo={!statesRestored}
              />
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                {colorCodeEnabled ? (
                  <div className="flex justify-center gap-2">
                    {getStateKeys(routeStateCount).map((k) => (
                      <span
                        key={k}
                        className="rounded-full border px-3 py-1.5 text-sm font-medium text-slate-900"
                        style={{ backgroundColor: STATUS_META[k].bg }}
                      >
                        {STATUS_META[k].name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-xs text-slate-400">Botones de estado ocultos</p>
                )}
              </div>
            </SettingsCard>
            {statesRestored ? (
              <CoachMark text="Perfecto. Los botones de estado vuelven a estar disponibles para el equipo." />
            ) : (
              <CoachMark
                text={
                  colorCodeEnabled
                    ? 'Desactiva los botones de estado para ver cómo desaparecen.'
                    : 'Vuelve a activarlos para ver cómo aparecen de nuevo.'
                }
              />
            )}
            {statesRestored ? <ContinueButton onClick={handleContinue} /> : null}
          </div>
        );
      }

      if (step.id === 'state-count') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Estados de ruta">
              <p className="text-sm font-medium text-foreground">Cantidad de Estados de Ruta</p>
              <div className="flex gap-3">
                {['2', '3', '4'].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      if (n === '2') {
                        setRouteStateCount('2');
                        setStateCountReturnPrompt(true);
                      } else if (n === '4') {
                        setRouteStateCount('4');
                        setStateCountReturnPrompt(false);
                        goNext();
                      }
                    }}
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-sm text-foreground',
                      routeStateCount === n && 'bg-muted',
                      (!stateCountReturnPrompt && n === '2') && 'ring-4 ring-amber-400 ring-offset-2',
                      (stateCountReturnPrompt && n === '4') && 'ring-4 ring-amber-400 ring-offset-2'
                    )}
                  >
                    {n} Estados
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                {getStateKeys(routeStateCount).map((k, i) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-16 text-xs text-muted-foreground">Estado {i + 1}</span>
                    <span className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground">
                      {STATUS_META[k].name}
                    </span>
                    <span className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground">
                      {STATUS_META[k].colorName}
                    </span>
                  </div>
                ))}
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex justify-center gap-2">
                  {getStateKeys(routeStateCount).map((k) => (
                    <span
                      key={k}
                      className="rounded-full border px-3 py-1.5 text-sm font-medium text-slate-900"
                      style={{ backgroundColor: STATUS_META[k].bg }}
                    >
                      {STATUS_META[k].name}
                    </span>
                  ))}
                </div>
              </div>
            </SettingsCard>
            <CoachMark
              text={
                stateCountReturnPrompt
                  ? 'Ahora vuelve a 4 Estados.'
                  : 'Pulsa 2 Estados para ver cómo se simplifica la configuración.'
              }
            />
          </div>
        );
      }

      if (step.id === 'state-config-info') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Estados de ruta">
              <div className="space-y-2">
                {getStateKeys('4').map((k, i) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-16 text-xs text-muted-foreground">Estado {i + 1}</span>
                    <span className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground">
                      {STATUS_META[k].name}
                    </span>
                    <span className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground">
                      {STATUS_META[k].colorName}
                    </span>
                  </div>
                ))}
              </div>
            </SettingsCard>
            <CoachMark text="El administrador puede personalizar tanto el nombre como el color de cada estado." />
            <ContinueButton onClick={handleContinue} />
          </div>
        );
      }

      if (step.id === 'admin-pin-info') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Crear PIN de administrador">
              <p className="text-xs text-muted-foreground">
                Protege el acceso a esta pantalla de Configuración general.
              </p>
              <div>
                <p className="text-sm font-medium text-foreground">PIN nuevo (4 a 6 dígitos)</p>
                <div className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
                  ••••
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Repetir PIN nuevo</p>
                <div className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
                  ••••
                </div>
              </div>
              <Button type="button" variant="outline">
                Crear PIN de administrador
              </Button>
            </SettingsCard>
            <CoachMark text="El PIN de administrador protege el acceso a esta configuración general." />
            <ContinueButton onClick={handleContinue} />
          </div>
        );
      }

      if (step.id === 'plans-action') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <SettingsCard title="Suscripción">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">Plan actual</p>
                  <p className="font-semibold text-foreground">Premium Pro</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setSettingsOpen(false);
                    setPlansOpen(true);
                    goNext();
                  }}
                  className="bg-yellow-500 text-white ring-4 ring-amber-400 ring-offset-2 hover:bg-yellow-600"
                >
                  Ver planes y precios
                </Button>
              </div>
            </SettingsCard>
            <CoachMark text="Pulsa Ver planes y precios para ajustar Premium Pro al tamaño de tu equipo." />
          </div>
        );
      }

      if (step.id === 'settings-overview') {
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">Configuración</h3>
            <CoachMark text="Aquí puedes configurar tu empresa, gestionar perfiles y controlar cómo trabaja y se comunica tu equipo." />
            <ContinueButton onClick={handleContinue} />
            <SettingsCard title="Datos de Empresa">
              <div>
                <p className="text-sm font-medium text-foreground">Nombre de la Empresa</p>
                <div className="mt-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground">
                  {savedCompanyName}
                </div>
              </div>
            </SettingsCard>
            <SettingsCard title="Edición de perfiles">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Perfiles</p>
                <Button type="button" size="sm">
                  <Plus className="mr-2 h-4 w-4" /> Añadir
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {PROFILES.map((p) => (
                  <div key={p.id} className="rounded-md border bg-muted/30 p-2 text-sm text-foreground">
                    {p.id}
                  </div>
                ))}
              </div>
            </SettingsCard>
            <SettingsCard title="Vincular cuenta">
              <p className="text-xs text-muted-foreground">
                La cuenta debe haberse registrado y verificado su correo antes de vincularla.
              </p>
            </SettingsCard>
            <SettingsCard title="Visibilidad entre perfiles">
              <SwitchRow label="Hacer visibles todos los perfiles y sus estados" checked onToggle={() => {}} />
              <div className="border-t" />
              <SwitchRow label="Permitir consultar los clientes de otros perfiles" checked onToggle={() => {}} />
            </SettingsCard>
            <SettingsCard title="Comunicación entre perfiles">
              <SwitchRow label="Compartir acceso directo a WhatsApp" checked onToggle={() => {}} />
              <div className="border-t" />
              <SwitchRow label="Compartir avisos de flota" checked onToggle={() => {}} />
              <SwitchRow label="Activar botones de estado entre perfiles" checked onToggle={() => {}} />
            </SettingsCard>
            <SettingsCard title="Estados de ruta">
              <p className="text-sm text-muted-foreground">Cantidad de Estados de Ruta</p>
              <div className="flex gap-3">
                <span className="text-sm text-foreground">2 Estados</span>
                <span className="text-sm text-foreground">3 Estados</span>
                <span className="text-sm text-foreground">4 Estados</span>
              </div>
            </SettingsCard>
            <SettingsCard title="Crear PIN de administrador">
              <p className="text-xs text-muted-foreground">
                Protege el acceso a esta pantalla de Configuración general.
              </p>
            </SettingsCard>
            <SettingsCard title="Suscripción">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">Plan actual</p>
                  <p className="font-semibold text-foreground">Premium Pro</p>
                </div>
                <Button type="button" size="sm" className="bg-yellow-500 text-white hover:bg-yellow-600">
                  Ver planes y precios
                </Button>
              </div>
            </SettingsCard>
            <SettingsCard title="Sesión">
              <Button type="button" variant="destructive" className="w-full">
                <LogOut className="mr-2 h-4 w-4" /> Cerrar Sesión
              </Button>
            </SettingsCard>
            <CoachMark text="Aquí puedes configurar tu empresa, gestionar perfiles y controlar cómo trabaja y se comunica tu equipo." />
            <ContinueButton onClick={handleContinue} />
          </div>
        );
      }
    }

    if (step.id === 'company-result') {
      return (
        <div className="space-y-3">
          {renderMainScreen(false)}
          <CoachMark text="El nombre actualizado se mostrará en la pantalla principal." />
          <ContinueButton
            onClick={() => {
              setSettingsOpen(true);
              goNext();
            }}
          />
        </div>
      );
    }

    if (step.id === 'tutorial-complete') {
      return <SimulatedComplete onRestartTutorial={restart} onExitTutorial={completeAction} exitLabel={completeLabel} />;
    }

    return null;






  };

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Acciones de administrador</DialogTitle>
        <DialogDescription>
          Configura tu empresa, gestiona perfiles y cuentas, define la visibilidad y
          comunicación del equipo y adapta Premium Pro a tu equipo en un entorno de práctica.
        </DialogDescription>
      </DialogHeader>

      <TutorialEngine
        steps={STEPS}
        renderStep={renderStep}
        onExit={onExit}
        onRestart={resetScenario}
        onBack={handleBack}
        showStepNavigation={false}
      />
    </div>
  );
}
